import { baseApi } from '@/utils/requestConfig'
// eslint-disable-next-line
import { Response } from '../types/axios'
// eslint-disable-next-line
import { CombiTimeslotDto, GroupedAppointments, OtbAppointment } from '../types/otbBeratung'

const API_BASE_URL = '/otb-beratung'

/**
 * Get the past (within timespan or default 10 years) and upcoming appointments for the given CKNR or email address
 *
 * @param {string} cknr - CKNR of the user
 * @param {string} email - email address of the user
 * @param {number} timespan -The timespan in years to look back for past appointments. Default is 10 years if not provided.
 * @returns {Promise<Response<OtbAppointment[]>>} A promise that resolves to an array of appointment objects.
 */
export function getAppointments(cknr, email, timespan) {
	return baseApi.get('/otb-beratung/appointments', {
		params: { cknr: cknr, 'E-Mail': email, timespan: timespan },
	})
}

/**
 * Get the past (within timespan or default 10 years) and upcoming appointments for the given CKNR or email address
 * @param {string} cknr - CKNR of the user
 * @param {string} email - email address of the user
 * @param {number} timespan -The timespan in years to look back for past appointments. Default is 10 years if not provided.
 * @returns {Promise<Response<GroupedAppointments>>} A promise that resolves to an object with past and upcoming appointments.
 */
export function getGroupedAppointments(cknr, email, timespan) {
	return baseApi.get('/otb-beratung/appointments/grouped', {
		params: { cknr: cknr, 'E-Mail': email, timespan: timespan },
	})
}

/**
 * Get the iCal (.ics) file for the given appointment
 * @param {string} appointmentId - The UUID of the appointment
 * @returns {Promise<Response<Blob>>} A promise that resolves to a blob of the iCal file.
 */
export function getCalender(appointmentId) {
	return baseApi.get('/otb-beratung/appointments/' + appointmentId + '/download/calender', {
		responseType: 'blob',
	})
}

/**
 * Cancel/delete an existing OTB appointment
 * @param {string} appointmentId - The UUID of the appointment to cancel
 * @returns {Promise<Response<Object>>} A promise that resolves when the appointment is successfully canceled
 * @throws {Error} 400 - Invalid appointment ID format
 * @throws {Error} 404 - Appointment with given ID not found
 * @throws {Error} 409 - Appointment cannot be canceled (e.g., too close to appointment time)
 */
export function cancelAppointment(appointmentId) {
	return baseApi.get(`${API_BASE_URL}/appointments/cancel/${appointmentId}`)
}


/**
 * Book a new appointment in the OTB system
 * 
 * @param {Object} appointmentData - The appointment data for booking
 * @param {string} appointmentData.extId - The globalStpId of the site
 * @param {string} appointmentData.startDateTimeUTC - The start date and time in UTC (ISO 8601 format)
 * @param {string} appointmentData.endDateTimeUTC - The end date and time in UTC (ISO 8601 format)
 * @param {number} appointmentData.lvnr - The LVNR of the user
 * @param {string} [appointmentData.cknr] - The CKNR of the user
 * @param {string} [appointmentData.phone] - The phone number of the user
 * @param {string} [appointmentData.email] - The email address of the user
 * @param {Array<string>} appointmentData.topicIds - The SNR numbers of the services
 * @returns {Promise<Response<OtbAppointment>>} A promise that resolves to the created appointment
 * @throws {Error} 400 - Wrong date inputs provided or wrong email format
 * @throws {Error} 401 - No or wrong authentication provided
 * @throws {Error} 403 - Authentication provided, but not authorized to access this resource
 * @throws {Error} 404 - The given globalStpId is not found or member with given CKNR not found
 * @throws {Error} 409 - The requested time slot is not available anymore
 */
export function bookKombiBuchung(appointmentData) {
	return baseApi.post(`${API_BASE_URL}/appointments/kombi-buchung`, appointmentData)
}

/**
 * Get available time slots for kombi-buchung appointments in the specified time range
 * 
 * @param {string} globalStpId - The globalStpId of the site
 * @param {string} startDateTime - The start date and time in UTC (ISO 8601 format)
 * @param {string} endDateTime - The end date and time in UTC (ISO 8601 format)
 * @param {number} lvnr - The LVNR of the user
 * @param {string} cknr - The CKNR of the user
 * @param {string} email - The email address of the user
 * @param {Array<string>} topicIds - The SNR number of the servic
 * @returns {Promise<Response<CombiTimeslotDto[]>>} A promise that resolves to an array of available time slots
 * @throws {Error} 400 - Wrong date inputs provided
 * @throws {Error} 404 - The given globalStpId is not found
 */
export function getAvailableTimeslots(globalStpId, startDateTime, endDateTime, lvnr, cknr, email, topicIds) {
	return baseApi.get(`${API_BASE_URL}/appointments/kombi-buchung`, {
		params: {
			globalStpId,
			startDateTime: startDateTime,
			endDateTime: endDateTime,
			lvnr,
			cknr,
			email,
			topicIds: topicIds
		},
		paramsSerializer: function(params) {
			// Custom serializer to avoid [] notation for arrays
			const searchParams = new URLSearchParams()
			for (const key in params) {
				if (Array.isArray(params[key])) {
					params[key].forEach(value => {
						searchParams.append(key, value)
					})
				} else {
					searchParams.append(key, params[key])
				}
			}
			return searchParams.toString()
		}
	})
}
